import i18n from '@/libs/i18n'
import router from '@/router/instance'
import store from '@/store'
import useBasicToast from '@/composable/useBasicToast'
import useErrorManager from '@/composable/useErrorManager'
import useModalDialog from '@/composable/useModalDialog'
import { ref } from '@vue/composition-api'
import useUploadFile from './useUploadFile'
import { createSingleFlight } from './operationFeedback'
import { markSaved } from './useUnsavedChangesGuard'

export default function useEntityCrud(moduleName, endpoint, convertCb) {
  const { checkFilesToUpload, uploadFiles } = useUploadFile()
  const errorManager = useErrorManager()
  const toast = useBasicToast()
  const modal = useModalDialog()
  const refTable = ref(null)
  const deleteFlight = createSingleFlight()
  const form = ref({})
  const item = ref(null)
  const currentLanguage = ref('fa')
  const isSubmit = ref(false)
  const itemLoading = ref(false)
  const tags = ref([])
  const newTagIds = ref([])
  const userOptions = ref([])
  const userOptions1 = ref([])
  const userOptions2 = ref([])

  function convertCollection(list, lang) {
    return list.map(itemEntry => convertCb(itemEntry, lang))
  }
  function detectLanguage() {
    const { lang } = router.currentRoute.params
    if (lang) return lang
    return 'fa'
  }

  const fetchList = params => store
    .dispatch('crud/fetchList', {
      queryParams: {
        lang: currentLanguage.value,
        ...params,
      },
      endpoint,
    })
    .then(response => {
      const { data } = response
      return {
        data: convertCollection(data.data, currentLanguage.value),
        total: data.total,
      }
    })

  const addItem = (data, lang) => {
    isSubmit.value = true
    store
      .dispatch('crud/add', { data, endpoint, lang })
      .then(() => {
        markSaved()
        toast({ title: i18n.t('foundation.saved'), type: 'success' })
        router.push({ name: `pages-${moduleName}-list` })
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        isSubmit.value = false
      })
  }
  const AddTags = (data, lang) => {
    isSubmit.value = true
    store
      .dispatch('crud/add', { data, endpoint, lang })
      .then(() => {
        router.push({ name: `pages-${moduleName}-list` })
      })
      .catch(() => {
        toast({
          title: 'عنوان تکراری است.',
          type: 'danger',
        })
      })
      .finally(() => {
        isSubmit.value = false
      })
  }
  const updateItem = (id, data, lang) => {
    isSubmit.value = true
    store
      .dispatch('crud/update', { queryParams: { lang: currentLanguage.value }, data: { id, data, lang }, endpoint })
      .then(() => {
        markSaved()
        toast({ title: i18n.t('foundation.saved'), type: 'success' })
        router.push({ name: `pages-${moduleName}-list` })
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        isSubmit.value = false
      })
  }

  const fetchItem = id => {
    itemLoading.value = true
    return store
      .dispatch('crud/fetchItem', { queryParams: { lang: currentLanguage.value }, data: { id }, endpoint })
      .then(response => {
        item.value = response.data
        form.value = convertCb(response.data, detectLanguage(response.data))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        itemLoading.value = false
      })
  }

  const deleteItem = id => {
    modal({
      title: i18n.t('foundation.deleteTitle'),
      message: i18n.t('foundation.deleteMessage'),
      okTitle: i18n.t('foundation.deleteConfirm'),
    }).then(res => {
      if (!res) return undefined
      return deleteFlight(() => store
        .dispatch('crud/delete', { queryParams: { lang: currentLanguage.value }, data: { id }, endpoint })
        .then(() => {
          toast({ title: i18n.t('foundation.deleted'), type: 'success' })
        })
        .catch(err => {
          errorManager(err)
        })
        .finally(() => {
          // After an unknown result the list is reloaded so the user sees the real state.
          refTable.value.refetchData()
        }))
    })
  }
  const groupDelete = ids => {
    modal({
      title: i18n.t('foundation.deleteGroupTitle', { count: ids.length }),
      message: i18n.t('foundation.deleteGroupMessage', { count: ids.length }),
      okTitle: i18n.t('foundation.deleteConfirm'),
    }).then(res => {
      if (!res) return undefined
      isSubmit.value = true
      return deleteFlight(() => store
        .dispatch('crud/groupDelete', { data: { ids }, endpoint })
        .then(() => {
          toast({
            title: i18n.t('general.groupDeleteSuccess'),
            type: 'success',
          })
        })
        .catch(err => {
          errorManager(err)
        })
        .finally(() => {
          refTable.value.refetchData()
          isSubmit.value = false
        }))
    })
  }

  const changeLanguage = newLang => {
    currentLanguage.value = newLang
    refTable.value.refetchData()
  }

  const toEditPage = id => {
    router.push({
      name: `pages-${moduleName}-edit`,
      params: { id, lang: currentLanguage.value },
    })
  }

  function updateFileAlt(id, data, lang) {
    store
      .dispatch('crud/updateFileAlt', { data: { id, data, lang } })
      .catch(err => {
        errorManager(err)
      })
  }
  async function handleUpload(formEntry, uploadList) {
    const uploadTarget = formEntry
    isSubmit.value = true
    const uploadForm = uploadList.map(propName => {
      const result = {
        type: propName,
        value: formEntry[propName],
        lang: formEntry.lang,
      }
      if (formEntry[`${propName}Alt`]) {
        if (typeof formEntry[`${propName}Alt`] === 'object') {
          result.alt = formEntry[`${propName}Alt`][formEntry.lang]
        } else if (typeof formEntry[`${propName}Alt`] === 'string') {
          result.alt = formEntry[`${propName}Alt`]
        } else {
          result.alt = ''
        }
      }
      return result
    })
    const { filesToUpload, filesNoUpload } = checkFilesToUpload(uploadForm)
    if (filesToUpload.length) {
      const result = await uploadFiles(filesToUpload)
      if (result) {
        result.forEach(({ value, type }) => {
          const assignmentResult = value
          uploadTarget[type] = assignmentResult; return assignmentResult
        })
      }
      isSubmit.value = false
    }
    filesNoUpload.forEach(filesNoUploadElement => {
      updateFileAlt(
        filesNoUploadElement.value && filesNoUploadElement.value._id,
        { alt: filesNoUploadElement.alt },
        formEntry.lang,
      )
    })
    return formEntry
  }

  const fetchTags = (lang, search = '') => {
    store
      .dispatch('crud/fetchList', {
        queryParams: { search, lang },
        endpoint: 'tags',
      })
      .then(response => {
        tags.value = response.data.data.map(itemEntry2 => ({
          _id: itemEntry2._id,
          title: itemEntry2.title,
        }))
      })
      .catch(err => {
        errorManager(err)
      })
  }

  const addTag = (data, lang) => {
    isSubmit.value = true
    return store
      .dispatch('crud/add', { data, endpoint: 'tags', lang })
      .then(response => {
        newTagIds.value.push(response.data._id)
        toast({
          title: 'برچسب با موفقیت افزوده شد',
          type: 'success',
        })
      })
      .catch(() => {
        // errorManager(err)
        toast({
          title: 'برچسب تکراری می باشد',
          type: 'danger',
        })
      })
      .finally(() => {
        isSubmit.value = false
      })
  }

  const fetchUsersByRole = params => {
    userOptions.value = []
    itemLoading.value = true
    return store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang: currentLanguage.value,
          ...params,
        },
        endpoint: '/users',
      })
      .then(response => {
        userOptions.value = response.data.data.map(itemEntry3 => ({ _id: itemEntry3._id, fullName: `${itemEntry3.fullName} - ${itemEntry3.phoneNumber}` }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchUsersByRole1 = params => {
    userOptions1.value = []
    itemLoading.value = true
    return store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang: currentLanguage.value,
          ...params,
        },
        endpoint: '/users',
      })
      .then(response => {
        userOptions1.value = response.data.data.map(itemEntry4 => ({ _id: itemEntry4._id, fullName: `${itemEntry4.fullName} - ${itemEntry4.phoneNumber}` }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchUsersByRole2 = params => {
    userOptions2.value = []
    itemLoading.value = true
    return store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang: currentLanguage.value,
          ...params,
        },
        endpoint: '/users',
      })
      .then(response => {
        userOptions2.value = response.data.data.map(itemEntry5 => ({ _id: itemEntry5._id, fullName: `${itemEntry5.fullName} - ${itemEntry5.phoneNumber}` }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const addNewTag = (lang, tag) => {
    if (tag && typeof tag !== 'string') {
      if (tag.title && typeof tag.title === 'string') {
        return addTag({ title: tag.title }, lang)
      }
      toast({
        title: 'ساختار برچسب اشتباه است',
        type: 'danger',
      })
    } else if (tag && typeof tag === 'string') {
      return addTag({ title: tag }, lang)
    } else {
      toast({
        title: 'ساختار برچسب اشتباه است',
        type: 'danger',
      })
    }
    return undefined
  }

  return {
    addItem,
    addNewTag,
    addTag,
    changeLanguage,
    convertCollection,
    currentLanguage,
    deleteItem,
    fetchItem,
    fetchList,
    fetchTags,
    fetchUsersByRole,
    fetchUsersByRole1,
    fetchUsersByRole2,
    userOptions,
    userOptions1,
    userOptions2,
    form,
    groupDelete,
    handleUpload,
    isSubmit,
    item,
    itemLoading,
    newTagIds,
    refTable,
    tags,
    AddTags,
    toEditPage,
    updateItem,
  }
}
